import { Badge, Box, Button, Card, Flex, Grid, Inset } from '@radix-ui/themes'
import { Link, useOutletContext } from '@remix-run/react'
import cn from 'clsx'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { ProductName } from '#app/components/common/product-name'
import { WishlistAction } from '#app/components/common/wishlist-action'
import { Image, IMAGE_FILTERS } from '#app/components/ui/image.tsx'
import { Price } from '#app/components/ui/price/price.tsx'
import { Rating } from '#app/components/ui/rating/rating.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { type ProductVariantNormalized } from '#app/models/ecommerce-providers/sylius/shop/interface/product-variant.ts'
import { type Context } from '#app/routes/_layout/route-config.ts'
import { testId } from '#app/utils/client/test-id.ts'

/* -------------------------------------------------------------------------------------------------
 * I18next
 * -----------------------------------------------------------------------------------------------*/

enum I18NEXT {
	uiProductGridItem = 'ui-product-grid-item',
}

/* -------------------------------------------------------------------------------------------------
 * TEST_IDS
 * -----------------------------------------------------------------------------------------------*/

enum TEST_ID {
	purchasable = 'purchasable',
	'product-item' = 'product-item',
}

/* -------------------------------------------------------------------------------------------------
 * ProductGridItem
 * -----------------------------------------------------------------------------------------------*/

type ProductGridItemProps = {
	product: GetProduct
	inWishlist: boolean
	displayWishlist?: boolean
	className?: string
}

const ProductGridItem: React.FC<ProductGridItemProps> = (props) => {
	const { product, inWishlist, displayWishlist = true, className } = props

	const { locale } = useOutletContext<Context>()
	const { t } = useTranslation(I18NEXT.uiProductGridItem)

	return (
		<Card
			asChild
			size="3"
			className={cn('app-GridItem appImageShadowContainer', className)}
			data-purchasable={product.inStock}
			{...testId(TEST_ID['product-item'])}
		>
			<li>
				{!product.inStock && false && (
					<Inset clip="padding-box" side="top" pb="current">
						<Box position="absolute" top="-4px">
							<Badge color="red">{t('items.badge.soldOut')}</Badge>
						</Box>
					</Inset>
				)}
				{displayWishlist && (
					<Box position="absolute" top="0" right="0" className="z-10">
						<WishlistAction product={product} inWishlist={inWishlist} />
					</Box>
				)}
				<Grid asChild columns="5" gapX="4" position="relative" mt="1">
					<Link
						prefetch="viewport"
						to={`/${locale}/products/${product.slug}`}
						aria-labelledby={`three-product-grid-item-${React.useId()}`}
					>
						<Flex align="center" className="col-span-2">
							<Image
								alt={product.name!}
								src={product.images![0].path!}
								filter={IMAGE_FILTERS.gridFilter}
								className="app-ImageShadow max-h-40"
								loading="lazy"
							/>
						</Flex>
						<Flex
							direction="column"
							justify="center"
							align="center"
							gapY="2"
							width="100%"
							className="col-span-3"
						>
							<ProductName
								name={product.name!}
								mt="3"
								ml="-8"
								align="center"
								size={{
									initial: '3',
									xs: '5',
									sm: '3',
									md: '5',
								}}
							/>
							<Flex justify="between" align="center" gapX="4" className="grow">
								<Rating>{product.averageRating!}</Rating>
								<Price
									locale={locale}
									color="gray"
									weight="bold"
									size={{
										initial: '2',
										md: '3',
									}}
								>
									{(product.defaultVariant as ProductVariantNormalized).price!}
								</Price>
							</Flex>
							<Box mt="3">
								<Button variant="ghost" size="2">
									{t('items.button.product')}
								</Button>
							</Box>
						</Flex>
					</Link>
				</Grid>
			</li>
		</Card>
	)
}
ProductGridItem.displayName = 'ProductGridItem'

/* -----------------------------------------------------------------------------------------------*/

export {
	ProductGridItem,
	I18NEXT as PRODUCT_GRID_ITEM_I18NEXT,
	TEST_ID as PRODUCT_GRID_ITEM_TEST_ID,
}
